import {
  ChangeDetectorRef,
  Component,
  NgZone,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Socket } from 'socket.io-client';
import { EVT_UserVideoApp } from 'lingo2-api-models';
import { Subscription } from 'rxjs';
import {
  OCMediaDeviceService,
  OCMediaRecorderService,
  OCMediaElementService,
  OCMediaStreamService,
  OCTimeCodeTimer,
  MediaDeviceType,
  DevicePermissionState,
} from '@onclass/oc-mediadevice';
import { CommonModule } from '@angular/common';
import { FrameAuthService } from 'src/app/services/frame-auth.service';
import { describeAccessToken } from 'src/app/services/frame-auth';
import { videoSocket } from 'src/app/services/video-socket';

const VIDEO_CANCEL = 'uservideoapp:video:cancel';
const VIDEO_LIMITS = 'uservideoapp:video:limits';
const CHUNK_BYTES = 512 * 1024;
const MAX_CHUNK_ATTEMPTS = 5;
const UPLOAD_DEADLINE_MS = 60_000;
const VIDEO_END_TIMEOUT_MS = 10_000;

@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'user-video',
  templateUrl: './user-video.component.html',
  styleUrls: ['./user-video.component.scss'],
})
export class UserVideoComponent implements OnInit, OnDestroy {
  @Output() onEvent = new EventEmitter<string>();
  @ViewChild('video', { static: false })
  videoRef!: ElementRef<HTMLVideoElement>;

  public busy = false;
  public error = null;
  public permissions = true;
  public canConfirm = false;
  public playback = false;
  public timerText = '';
  public minDurationSeconds = 0;
  public maxDurationSeconds = 0;
  public authMissing = false;
  public authPending = true;

  private socket: Socket | null = null;
  private socketConnectedAt = 0;
  private droppedChunks = 0;
  private mediaFormat: { container: string | null; codec: string | null; mimeType: string | null } | null = null;
  private recorderSub!: Subscription;
  private recorderStopSub!: Subscription;
  private permissionSub = new Subscription();
  private previewTask: Promise<void> | null = null;
  private authFailurePending = false;
  private confirming = false;
  private recordingId: string | null = null;
  private chunkQueue: { seq: number; chunk: unknown }[] = [];
  private nextChunkSeq = 0;
  private flushingChunks = false;
  private endPending = false;
  private captureReleaseTimer: ReturnType<typeof setTimeout> | null = null;
  private finishTimer: ReturnType<typeof setTimeout> | null = null;
  private uploadTimer: ReturnType<typeof setTimeout> | null = null;
  private chunkAttempts = 0;
  private recorderStopped = false;
  private recordingFinished = false;
  private uploadAborted = false;
  private videoEndSent = false;
  private chunksBeforeStop = 0;
  private bytesBeforeStop = 0;
  private mediaChunksAfterStop = 0;
  private timer = new OCTimeCodeTimer((formatted) => {
    this.timerText = formatted;
    this.cd.detectChanges();
  });

  private postToParent(type: string, data?: any) {
    this.frameAuth.postAppMessage(type, data);
  }

  private logSocket(event: string, data?: any) {
    console.debug(`[UserVideoComponent][ws] ${event}`, {
      id: this.socket?.id ?? null,
      transport: this.socket?.io?.engine?.transport?.name ?? null,
      uptime: this.socketConnectedAt
        ? `${((Date.now() - this.socketConnectedAt) / 1000).toFixed(1)}s`
        : null,
      data: data ?? null,
    });
  }

  public width: number = 0;
  public height: number = 0;

  constructor(
    private cd: ChangeDetectorRef,
    private md_srv: OCMediaDeviceService,
    private rec_srv: OCMediaRecorderService,
    private stream_srv: OCMediaStreamService,
    private el_srv: OCMediaElementService,
    private frameAuth: FrameAuthService,
    private zone: NgZone
  ) {}

  public recording = false;

  public isRecording = () => this.recording;

  public get minDurationText(): string {
    return this.formatDuration(this.minDurationSeconds);
  }

  public get maxDurationText(): string {
    return this.formatDuration(this.maxDurationSeconds);
  }

  public get durationRangeText(): string {
    if (!this.minDurationSeconds || !this.maxDurationSeconds) {
      return '';
    }
    return `${this.minDurationText} – ${this.maxDurationText}`;
  }

  private formatDuration(seconds: number): string {
    const total = Math.max(0, Math.round(seconds));
    const minutes = Math.floor(total / 60);
    const rest = total % 60;
    return `${minutes}:${rest.toString().padStart(2, '0')}`;
  }

  private applyDurationLimits(payload?: { minSeconds?: number; maxSeconds?: number }) {
    const minSeconds = Number(payload?.minSeconds);
    const maxSeconds = Number(payload?.maxSeconds);
    if (minSeconds > 0) {
      this.minDurationSeconds = minSeconds;
    }
    if (maxSeconds > 0) {
      this.maxDurationSeconds = maxSeconds;
    }
  }

  public get hasAccess(): boolean {
    return !!this.frameAuth.getAccess();
  }

  async ngOnInit() {
    const params = new URLSearchParams(window.location.search);
    this.width = Number(params.get('w'));
    this.height = Number(params.get('h'));
    console.debug(
      '[UserVideoComponent]: starting with w/h=',
      this.width,
      this.height
    );

    this.busy = false;
    this.frameAuth.start();
    this.frameAuth.onLogout(() => this.handleLogout());
    this.frameAuth.onAccessChanged(() => this.handleAccessChanged());
    this.watchPermissons();
    void this.requestMediaAccess();
    this.postToParent('init', { width: this.width, height: this.height });

    await this.frameAuth.ready();
    this.authPending = false;
    if (this.frameAuth.getAccess()) {
      this.authMissing = false;
      if (!this.socket) {
        this.connectSocket();
      }
    } else {
      this.authMissing = true;
    }
    this.cd.detectChanges();
  }

  getStyle() {
    if (this.width > 0 && this.height > 0)
      return `width: ${this.width}px; height: ${this.height}px`;
    return '';
  }

  ngOnDestroy() {
    this.stopRecording();
    this.rec_srv.destroy();
    this.permissionSub.unsubscribe();
    this.recorderSub?.unsubscribe();
    this.recorderStopSub?.unsubscribe();
    if (this.finishTimer) {
      clearTimeout(this.finishTimer);
      this.finishTimer = null;
    }
    this.clearUploadDeadline();
    if (this.captureReleaseTimer) {
      clearTimeout(this.captureReleaseTimer);
      this.captureReleaseTimer = null;
    }
    this.teardownSocket();
    this.frameAuth.stop();
    this.stream_srv.stopStream('video');
    this.stream_srv.stopStream('audio');
  }

  private getMimeType(): MediaRecorderOptions {
    const types = [
      'video/mp4',
      'video/webm',
    ];
    const type = types.find(MediaRecorder.isTypeSupported);
    return type ? { mimeType: type } : {};
  }

  private describeMedia(mimeType?: string) {
    const container = mimeType?.split(';')[0] ?? null;
    const declared = mimeType?.match(/codecs=([^;]+)/i)?.[1] ?? null;
    const probes = [
      'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
      'video/mp4;codecs=avc1,mp4a.40.2',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp8',
    ];
    const supported = probes.filter((candidate) => {
      try {
        return candidate.startsWith(container ?? '') && MediaRecorder.isTypeSupported(candidate);
      } catch {
        return false;
      }
    });
    const codec = declared ?? (supported[0]?.split('codecs=')[1] ?? null);
    return { container, codec, mimeType: mimeType ?? null, supportedCodecs: supported };
  }

  watchPermissons() {
    this.permissionSub.add(
      this.md_srv.getPermissionState$(MediaDeviceType.CAMERA).subscribe((state) => {
        if (state === DevicePermissionState.GRANTED) {
          this.permissions = true;
          this.cd.detectChanges();
          this.postToParent('permissions_granted');
          void this.startCamera();
        } else if (state === DevicePermissionState.DENIED) {
          this.denyCameraAccess();
        }
      }),
    );
    this.permissionSub.add(
      this.md_srv.getPermissionState$(MediaDeviceType.MIC).subscribe((state) => {
        if (state === DevicePermissionState.GRANTED) {
          void this.attachMicrophone();
        }
      }),
    );
  }

  private async requestMediaAccess() {
    const granted = await this.md_srv.requestPermissions({ video: true, audio: true });
    if (granted) {
      await this.startCamera();
    }
  }

  private clearVideoElement(video?: HTMLVideoElement) {
    if (!video) {
      return;
    }
    try {
      video.pause();
      video.srcObject = null;
      video.removeAttribute('src');
      video.controls = false;
      video.load();
    } catch (err) {
      console.warn('Video reset error:', err);
    }
  }

  private attachPreview(video: HTMLVideoElement, stream: MediaStream) {
    video.pause();
    video.removeAttribute('src');
    video.srcObject = stream;
    video.controls = false;
    video.muted = true;
    void video.play().catch((err) => console.warn('Video play error:', err));
  }

  private denyCameraAccess() {
    this.permissions = false;
    this.cd.detectChanges();
    this.stopRecording();
    this.stream_srv.stopStream('video');
    this.stream_srv.stopStream('audio');
    this.clearVideoElement(this.videoRef?.nativeElement);
    this.postToParent('permissions_denied');
  }

  private async attachMicrophone() {
    if (!this.stream_srv.getActiveStream('video') || this.stream_srv.getActiveStream('audio')) {
      return;
    }
    try {
      await this.stream_srv.initStream('audio');
    } catch (err) {
      console.warn('Audio stream init error:', err);
    }
  }

  private isPermissionDenied(err: unknown): boolean {
    const name = (err as { name?: string })?.name;
    return name === 'NotAllowedError' || name === 'PermissionDeniedError';
  }

  private connectSocket() {
    const token = this.frameAuth.getAccess();
    if (!token) {
      return;
    }
    console.debug('[UserVideoComponent][auth] connect', describeAccessToken(token));
    this.teardownSocket();
    this.socket = videoSocket.connect(token);
    this.bindSocket();
  }

  private teardownSocket() {
    if (!this.socket) {
      return;
    }
    this.socket.removeAllListeners();
    this.socket.io?.removeAllListeners?.();
    this.socket.disconnect();
    this.socket = null;
    this.socketConnectedAt = 0;
  }

  private bindSocket() {
    if (!this.socket) {
      return;
    }

    const stopWithFlag = (val?: string) => {
      this.busy = false;
      this.confirming = false;
      this.error = val || 'duration';
      this.stopRecording();
    };

    this.initTransportDebug();

    this.socket.on(VIDEO_LIMITS, (payload?: { minSeconds?: number; maxSeconds?: number }) => {
      this.applyDurationLimits(payload);
      this.cd.detectChanges();
    });
    this.socket.on(EVT_UserVideoApp.VIDEO_TOO_SHORT, (payload?: any) => {
      this.applyDurationLimits(payload);
      stopWithFlag();
      this.postToParent('video_too_short', payload || {});
    });
    this.socket.on(EVT_UserVideoApp.VIDEO_LIMIT_REACHED, () => {
      this.busy = false;
      this.stopRecording();
      this.postToParent('video_limit_reached');
    });
    this.socket.on(EVT_UserVideoApp.VIDEO_PROCESSING_ERROR, () => {
      this.chunkQueue = [];
      this.endPending = false;
      stopWithFlag('processing');
      this.postToParent('video_processing_error');
    });

    this.socket.on(EVT_UserVideoApp.VIDEO_READY, ({ url, need_confirm, recordingId }) => {
      this.endPending = false;
      if (recordingId) {
        this.recordingId = recordingId;
      }
      this.applyReadyVideo(url);
      this.canConfirm = !!need_confirm;
      this.busy = false;
      if (need_confirm) {
        this.confirming = false;
        this.postToParent('video_preview_ready', { url, need_confirm: true });
      } else {
        const wasConfirming = this.confirming;
        this.confirming = false;
        if (wasConfirming) {
          this.postToParent('video_confirmed', { url });
        }
        this.postToParent('video_ready', { url });
      }
      this.cd.detectChanges();
    });
  }

  private applyReadyVideo(url: string) {
    this.clearVideoElement(this.videoRef?.nativeElement);
    this.el_srv.loadFromUrl(this.videoRef?.nativeElement, url);
    try {
      const v = this.videoRef?.nativeElement;
      if (v) {
        (v as any).playsInline = true;
        (v as any).webkitPlaysInline = true;
        (v as any).disablePictureInPicture = true;
        v.muted = false;
        v.controls = true;
        this.playback = true;
      }
    } catch {}
  }

  private setPreviewMode() {
    this.playback = false;
    const v = this.videoRef?.nativeElement;
    if (!v) {
      return;
    }
    v.controls = false;
    v.muted = true;
  }

  private initTransportDebug() {
    if (!this.socket) {
      return;
    }
    this.logSocket('init', { url: 'ws' });

    this.socket.on('connect', () => {
      this.socketConnectedAt = Date.now();
      this.logSocket('connect', { recording: this.isRecording() });
      this.flushChunks();

      const engine = this.socket.io.engine;
      engine?.on('upgrade', () =>
        this.logSocket('transport_upgrade', { to: engine.transport?.name })
      );
      engine?.on('close', (reason: any) => {
        console.warn('[UserVideoComponent][ws] engine_close', {
          reason,
          recording: this.isRecording(),
          ...this.mediaFormat,
        });
        this.logSocket('engine_close', { reason, ...this.mediaFormat });
      });
    });

    this.socket.on('disconnect', (reason, description) => {
      const disconnectInfo = {
        reason,
        description,
        recording: this.isRecording(),
        droppedChunks: this.droppedChunks,
        ...this.mediaFormat,
      };
      console.warn('[UserVideoComponent][ws] disconnect', disconnectInfo);
      this.logSocket('disconnect', disconnectInfo);
      this.postToParent('ws_disconnect', {
        reason: String(reason),
        recording: this.isRecording(),
        container: this.mediaFormat?.container ?? null,
        codec: this.mediaFormat?.codec ?? null,
      });
      this.socketConnectedAt = 0;
      if (this.recording || this.busy || this.endPending || this.flushingChunks || this.chunkQueue.length) {
        this.abortUpload();
      }
      if (this.isAuthSocketError(reason)) {
        void this.handleAuthFailure();
      }
    });

    this.socket.on('connect_error', (err: any) => {
      console.error('[UserVideoComponent][ws] connect_error', {
        message: err?.message,
        description: err?.description,
        context: err?.context,
        transport: this.socket?.io?.engine?.transport?.name ?? null,
        token: describeAccessToken(this.frameAuth.getAccess()),
      });
      this.postToParent('ws_connect_error', {
        message: String(err?.message ?? err),
      });
      if (this.isAuthSocketError(err)) {
        void this.handleAuthFailure();
      }
    });

    this.socket.io.on('reconnect_attempt', (attempt) =>
      this.logSocket('reconnect_attempt', { attempt })
    );
    this.socket.io.on('reconnect', (attempt) =>
      this.logSocket('reconnect', { attempt, droppedChunks: this.droppedChunks })
    );
    this.socket.io.on('reconnect_error', (err: any) =>
      console.error('[UserVideoComponent][ws] reconnect_error', err?.message ?? err)
    );
    this.socket.io.on('reconnect_failed', () =>
      console.error('[UserVideoComponent][ws] reconnect_failed')
    );
    this.socket.io.on('error', (err: any) =>
      console.error('[UserVideoComponent][ws] manager_error', err?.message ?? err)
    );
  }

  private isAuthSocketError(errOrReason: unknown): boolean {
    const msg =
      typeof errOrReason === 'string'
        ? errOrReason
        : String((errOrReason as { message?: unknown })?.message ?? '');
    return msg === 'unauthorized' || /unauthorized|Authentication error/i.test(msg);
  }

  private async handleAuthFailure() {
    if (this.authFailurePending) {
      return;
    }
    this.authFailurePending = true;
    this.stopRecording();
    this.teardownSocket();
    const token = await this.frameAuth.requestRefreshedAccess();
    this.authFailurePending = false;
    if (token && !this.socket) {
      this.authMissing = false;
      this.connectSocket();
      return;
    }
    if (!token) {
      this.authMissing = true;
      this.postToParent('ws_connect_error', { message: 'unauthorized' });
      this.cd.detectChanges();
    }
  }

  private handleLogout() {
    this.stopRecording();
    this.discardRecording();
    this.teardownSocket();
    this.authMissing = true;
    this.canConfirm = false;
    this.confirming = false;
    this.setPreviewMode();
    this.cd.detectChanges();
  }

  private handleAccessChanged() {
    this.stopRecording();
    this.authMissing = false;
    this.connectSocket();
  }

  public confirmRecording() {
    if (!this.frameAuth.getAccess() || !this.socket || this.confirming) {
      return;
    }
    this.confirming = true;
    this.canConfirm = false;
    this.busy = true;
    this.socket.emit(EVT_UserVideoApp.VIDEO_CONFIRM, { recordingId: this.recordingId });
  }

  public cancelRecording() {
    this.stopRecording();
    this.discardRecording();
    this.canConfirm = false;
    this.confirming = false;
    this.busy = false;
    this.error = null;
    this.setPreviewMode();
    this.clearVideoElement(this.videoRef?.nativeElement);
    this.cd.detectChanges();
  }

  private discardRecording() {
    if (this.socket?.connected && this.recordingId) {
      this.socket.emit(VIDEO_CANCEL, { recordingId: this.recordingId });
    }
    this.recordingId = null;
    this.chunkQueue = [];
    this.nextChunkSeq = 0;
    this.endPending = false;
  }

  async startCamera() {
    if (this.previewTask) {
      return this.previewTask;
    }
    this.previewTask = this.openPreview();
    try {
      await this.previewTask;
    } finally {
      this.previewTask = null;
    }
  }

  private async openPreview() {
    this.busy = false;
    this.error = null;
    try {
      let stream = this.stream_srv.getActiveStream('video');
      if (!stream) {
        stream = await this.stream_srv.initStream('video');
      }
      this.permissions = true;
      this.cd.detectChanges();
      const video = this.videoRef?.nativeElement;
      if (video) {
        this.attachPreview(video, stream);
        video.playsInline = true;
        (video as HTMLVideoElement & { webkitPlaysInline?: boolean }).webkitPlaysInline = true;
        video.disablePictureInPicture = true;
        video.controls = false;
      }
      await this.attachMicrophone();
    } catch (err) {
      console.error(`Camera permissions error: `, err);
      this.stream_srv.stopStream('video');
      this.stream_srv.stopStream('audio');
      this.permissions = false;
      this.cd.detectChanges();
      if (this.isPermissionDenied(err)) {
        this.postToParent('permissions_denied');
      }
    }
  }

  async startRecording() {
    if (!this.frameAuth.getAccess() || !this.socket) {
      this.recording = false;
      this.timer.stop();
      this.cd.detectChanges();
      return;
    }

    if (this.rec_srv.isActive()) {
      return;
    }

    this.timer.start();

    if (!this.stream_srv.getActiveStream('video')) {
      await this.startCamera();
    }

    this.canConfirm = false;
    this.confirming = false;

    const videoStream = this.stream_srv.getActiveStream('video');
    let audioStream = this.stream_srv.getActiveStream('audio');
    if (!audioStream) {
      await this.attachMicrophone();
      audioStream = this.stream_srv.getActiveStream('audio');
    }

    if (!videoStream) {
      this.recording = false;
      this.timer.stop();
      this.cd.detectChanges();
      this.postToParent('record_start_error', { error: 'No video stream' });
      return;
    }

    const recordingStream = new MediaStream([
      ...videoStream.getVideoTracks(),
      ...(audioStream?.getAudioTracks() ?? []),
    ]);

    const mediaOptions = this.getMimeType();
    const media = this.describeMedia(mediaOptions.mimeType);
    this.mediaFormat = {
      container: media.container,
      codec: media.codec,
      mimeType: media.mimeType,
    };
    this.droppedChunks = 0;
    this.recordingId = null;
    this.chunkQueue = [];
    this.nextChunkSeq = 0;
    this.endPending = false;
    this.recorderStopped = false;
    this.recordingFinished = false;
    this.uploadAborted = false;
    this.videoEndSent = false;
    this.chunksBeforeStop = 0;
    this.bytesBeforeStop = 0;
    this.mediaChunksAfterStop = 0;
    this.chunkAttempts = 0;
    this.clearUploadDeadline();
    if (this.finishTimer) {
      clearTimeout(this.finishTimer);
      this.finishTimer = null;
    }
    console.info('[UserVideoComponent][media]', media);
    this.logSocket('video_start', media);
    const socket = this.socket;
    socket.timeout(10000).emit(EVT_UserVideoApp.VIDEO_START, {
      mimeType: mediaOptions.mimeType,
      container: media.container,
      codec: media.codec,
    }, (err: unknown, res?: { recordingId?: string; minSeconds?: number; maxSeconds?: number }) => {
      this.zone.run(() => {
        if (this.uploadAborted || socket !== this.socket) {
          return;
        }
        if (err || !res?.recordingId) {
          this.abortUpload();
          return;
        }
        this.applyDurationLimits(res);
        this.recordingId = res.recordingId;
        this.flushChunks();
        this.cd.detectChanges();
      });
    });
    this.postToParent('record_start');
    await this.rec_srv.startRecording(100, recordingStream, mediaOptions);
    this.recorderSub = this.rec_srv.data$.subscribe((buffer) => {
      this.enqueueRecordingBuffer(buffer instanceof ArrayBuffer ? buffer : new ArrayBuffer(0));
      if (this.recorderStopped) {
        this.scheduleFinish(400);
      }
    });

    this.recorderStopSub = this.rec_srv.onStop$.subscribe(() => {
      this.releaseCapture();
      this.recorderStopped = true;
      this.scheduleFinish(this.nextChunkSeq > 0 ? 3000 : 20000);
    });
  }

  private enqueueRecordingBuffer(buffer: ArrayBuffer) {
    const bytes = buffer.byteLength;
    if (!bytes) {
      return;
    }
    if (!this.recorderStopped) {
      this.chunksBeforeStop += 1;
      this.bytesBeforeStop += bytes;
    } else {
      this.mediaChunksAfterStop += 1;
    }
    for (let offset = 0; offset < bytes; offset += CHUNK_BYTES) {
      this.chunkQueue.push({
        seq: this.nextChunkSeq,
        chunk: buffer.slice(offset, Math.min(offset + CHUNK_BYTES, bytes)),
      });
      this.nextChunkSeq += 1;
    }
    this.flushChunks();
  }

  private releaseCapture() {
    if (this.captureReleaseTimer) {
      clearTimeout(this.captureReleaseTimer);
      this.captureReleaseTimer = null;
    }
    this.stream_srv.stopStream('video');
    this.stream_srv.stopStream('audio');
    this.clearVideoElement(this.videoRef?.nativeElement);
  }

  private scheduleFinish(delayMs: number) {
    if (this.recordingFinished || this.uploadAborted) {
      return;
    }
    if (this.finishTimer) {
      clearTimeout(this.finishTimer);
    }
    this.finishTimer = setTimeout(() => this.finishRecording(), delayMs);
  }

  private finishRecording() {
    if (this.recordingFinished) {
      return;
    }
    this.recordingFinished = true;
    if (this.finishTimer) {
      clearTimeout(this.finishTimer);
      this.finishTimer = null;
    }
    this.recorderSub?.unsubscribe();
    this.recorderStopSub?.unsubscribe();
    if (this.uploadAborted) {
      return;
    }
    console.info('[UserVideoComponent][chunk] upload', {
      parts: this.nextChunkSeq,
      chunksBeforeStop: this.chunksBeforeStop,
      chunksAfterStop: this.mediaChunksAfterStop,
      bytesBeforeStop: this.bytesBeforeStop,
      delivery: this.chunksBeforeStop > 0 ? 'parts_during_recording' : 'whole_file_at_end',
    });
    this.endPending = true;
    this.startUploadDeadline();
    this.flushChunks();
  }

  private startUploadDeadline() {
    if (this.uploadTimer || this.uploadAborted) {
      return;
    }
    this.uploadTimer = setTimeout(() => this.abortUpload(), UPLOAD_DEADLINE_MS);
  }

  private clearUploadDeadline() {
    if (!this.uploadTimer) {
      return;
    }
    clearTimeout(this.uploadTimer);
    this.uploadTimer = null;
  }

  private abortUpload() {
    if (this.uploadAborted) {
      return;
    }
    this.uploadAborted = true;
    this.clearUploadDeadline();
    this.busy = false;
    this.endPending = false;
    this.flushingChunks = false;
    this.chunkQueue = [];
    this.chunkAttempts = 0;
    this.error = 'processing';
    const recordingId = this.recordingId;
    if (this.socket?.connected && recordingId) {
      this.socket.emit(VIDEO_CANCEL, { recordingId });
    }
    this.recordingId = null;
    this.postToParent('record_start_error', { error: 'recording_start_failed' });
    if (this.recording || this.rec_srv.isActive()) {
      this.stopRecording();
    }
    this.busy = false;
    this.finishRecording();
    this.releaseCapture();
    this.cd.detectChanges();
  }

  private flushChunks() {
    if (this.uploadAborted || this.flushingChunks) {
      return;
    }
    if (!this.socket?.connected) {
      if (this.recording || this.endPending || this.recorderStopped || this.busy) {
        this.abortUpload();
      }
      return;
    }
    if (!this.recordingId) {
      return;
    }
    const item = this.chunkQueue[0];
    if (!item) {
      this.emitVideoEnd();
      return;
    }
    this.flushingChunks = true;
    const socket = this.socket;
    socket.timeout(10000).emit(
      EVT_UserVideoApp.VIDEO_CHUNK,
      { recordingId: this.recordingId, seq: item.seq, chunk: item.chunk },
      (err: unknown, res?: { ok?: boolean }) => {
        this.flushingChunks = false;
        if (this.uploadAborted || this.socket !== socket) {
          return;
        }
        if (err || !res?.ok) {
          this.chunkAttempts += 1;
          if (res?.ok === false || this.chunkAttempts >= MAX_CHUNK_ATTEMPTS || !this.socket?.connected) {
            this.abortUpload();
            return;
          }
          this.droppedChunks++;
          setTimeout(() => this.flushChunks(), 500);
          return;
        }
        this.chunkAttempts = 0;
        if (this.chunkQueue[0]?.seq === item.seq) {
          this.chunkQueue.shift();
        }
        this.flushChunks();
      },
    );
  }

  private emitVideoEnd() {
    if (this.videoEndSent || this.uploadAborted || !this.endPending || this.chunkQueue.length || this.flushingChunks) {
      return;
    }
    if (!this.recordingId || !this.socket?.connected) {
      this.abortUpload();
      return;
    }
    this.videoEndSent = true;
    const socket = this.socket;
    const recordingId = this.recordingId;
    socket.timeout(VIDEO_END_TIMEOUT_MS).emit(
      EVT_UserVideoApp.VIDEO_END,
      { recordingId },
      (err: unknown, res?: { ok?: boolean }) => {
        this.zone.run(() => {
          if (this.uploadAborted || socket !== this.socket) {
            return;
          }
          if (err || !res?.ok) {
            this.abortUpload();
            return;
          }
          this.clearUploadDeadline();
        });
      },
    );
  }

  stopRecording() {
    const active = this.rec_srv.isActive();
    this.recording = false;
    this.setPreviewMode();
    this.timer.stop();
    if (!active) {
      this.cd.detectChanges();
      return;
    }

    try {
      this.rec_srv.stopRecording();
      if (!this.uploadAborted) {
        this.busy = true;
      }
      this.postToParent('record_stop');
      this.captureReleaseTimer = setTimeout(() => this.releaseCapture(), 3000);
    } catch (err) {
      console.warn('Recorder stop error:', err);
      this.postToParent('record_stop_error', { error: String(err) });
      this.releaseCapture();
    }

    this.canConfirm = false;
    this.confirming = false;
    this.cd.detectChanges();
  }

  resetRecording() {
    this.stopRecording();
    this.clearVideoElement(this.videoRef?.nativeElement);
  }

  public toggleRecording() {
    if (this.recording) {
      this.stopRecording();
      return;
    }
    this.recording = true;
    void this.startRecording();
  }

  public settingsDialog() {
    this.onEvent.emit('settings');
  }

  public no_devices() {
    return false;
  }
}
